import React, { useState, useEffect } from "react";
import { LookupInput, type LookupInputOption, type LookupInputState } from "@/components/ui/LookupInput";
import { userService } from "@/services/userService";

interface UserInputProps {
  value: string | null;
  state?: LookupInputState;
  label?: string;
  error?: string;
  placeholder?: string;
  info?: string;
  showEmail?: boolean;
  onChange?: (userId: string | null) => void;
}

export function UserInput({
  value,
  state = "editing",
  label = "User",
  error,
  placeholder = "Select user\u2026",
  info,
  showEmail = true,
  onChange,
}: UserInputProps) {
  const [options, setOptions] = useState<LookupInputOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const users = await userService.getUsers();
        if (cancelled) return;
        setOptions(
          users.map((u) => ({
            id: u.id!,
            name:
              `${u.preferred_name || u.first_name || ""} ${u.last_name || ""}`.trim() || u.email_address || "Unknown",
            subtitle: showEmail ? u.email_address || undefined : undefined,
            avatar: {
              firstName: u.first_name ?? undefined,
              lastName: u.last_name ?? undefined,
              email: u.email_address ?? undefined,
              userId: u.id ?? undefined,
            },
          })),
        );
      } catch (err) {
        console.error("Failed to load users:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolvedState: LookupInputState = loading ? "loading" : state;

  return (
    <LookupInput
      label={label}
      value={value}
      options={options}
      state={resolvedState}
      icon="account"
      error={error}
      placeholder={placeholder}
      info={info}
      onChange={onChange}
    />
  );
}
